import {Component, computed, EventEmitter, inject, OnInit, Output, TemplateRef, ViewChild,} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {MenusServices} from '@app/services/menus.service';
import {BaseComponent} from '@app/components/base/base.component';
import {FormArray, FormBuilder, FormGroup, Validators} from '@angular/forms';
import {NotificationService} from '@app/services/notifications.service';
import {RolesInternal, UserType} from '@app/models/roles.enum';
import {StandardQuoteService} from './standard-quote-service.service';
import {ModalService} from '@app/components/modal/modal.service';
import {AuthService} from '@app/common/auth.service';
import {WholeSale} from "@app/core/model/CoreModel";
import {catchError, distinctUntilChanged, EMPTY, finalize, forkJoin, interval, map, of, Subject, switchMap, take, throwError, timer} from 'rxjs';
import {Dealer, DealerBranch} from "@app/pages/quote/standard-quote/model/dealer.model";
import { FinancialCalculatorService } from '@app/pages/quote/open-quote/services/financial-calculator.service';
import {
  statusType,
  isStandardQuoteCustomerOnlyLayout,
  isFinancialSetupCompanyMismatchForQuote,
} from '../open-quote/model/open-quote.model';
import { DealerService } from '@app/pages/dealers/services/dealer.service';
import { EntitiesServices } from '@app/services/entities.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ResponsiveService } from '@app/services/responsive.service';
import { createLoadingTracker } from '@app/core/util/loading-tracker';
import { ClientsService } from '@app/pages/clients/services/clients.service';
import { InsuranceTypeEnum } from '@app/services/insurance.service';
import { environment } from '@env/environment.local';
import { Constants } from '@app/constants/Constants';


const dealerBranchControlName = 'dealerBranch';
const dealerBranchIdControlName = 'wholeSaleId';
const dealerControlName = 'dealer';
const dealerIdControlName = 'dealerId';

const quantityValidators = [
  Validators.required,
  Validators.min(Constants.MIN_QUANTITY_FOR_NORMAL_PRODUCT),
  Validators.pattern(/^[1-9]\d*$/),
  Validators.max(Constants.MAX_QUANTITY_FOR_NORMAL_PRODUCT),
];

function normalizeQuoteQuantity(value: unknown): number {
  const n = Number(value);
  const min = Constants.MIN_QUANTITY_FOR_NORMAL_PRODUCT;
  return !Number.isFinite(n) || n < min ? min : Math.trunc(n);
}

@Component({
  selector: 'app-new-quote',
  templateUrl: './new-standard-quote.component.html',
  styleUrls: ['./new-standard-quote.component.scss'],
})
export class NewStandardQuoteComponent extends BaseComponent implements OnInit {
  @ViewChild('modalTemplate') modalTemplate!: TemplateRef<any>;
  @ViewChild('extendValidityModalTemplate') extendValidityModalTemplate!: TemplateRef<unknown>;
  @ViewChild('clientDesktop') clientDesktop: any;
  @ViewChild('clientMobile') clientMobile: any;
  @Output() requestOpenModal: EventEmitter<void> = new EventEmitter<void>();
  public readonly responsive = inject(ResponsiveService);
  public readonly clientsService = inject(ClientsService);

  statusAfterToDownloadPdf = '';
  form: FormGroup;
  role: RolesInternal;
  module: string = '';
  idQuote: any;
  quote = [];
  dealers: Dealer[] = [];
  accessories: any[] = [];
  isSetNullData: boolean = true;
  isValidForm: boolean = false;
  creationUser: string;
  isSelectedDeal: boolean = false;
  deals: any[] = [];
  id: any = 0;
  _quote: any;
  dealerBranches: DealerBranch[] = [];
  user: UserType;
  ready: Subject<any> = new Subject<any>();
  editOff: boolean = false;
  customerOnlyEditMode = false;
  /** True si la quote está vinculada a una solicitud de crédito: bloquear toda edición (incluye cliente). */
  linkedToCreditRequest = false;
  isSaved: string;
  saved: boolean;
  dealerId: number;
  dealerBranchName: string;
  noBranches: boolean = false;

  quoteValidityInfo: {
    valid?: boolean;
    minQuoteValidityDays?: number;
    validityEndDate?: string;
    validityExtensionApplied?: boolean;
    hasInactiveFinancialSetup?: boolean;
  } | null = null;
  extendingValidity = false;
  readonly showQuoteValidityUi = environment.showQuoteValidityListFilter;

  private readonly loading = createLoadingTracker();
  isLoading = computed(() => this.loading.count() > 0);
  private beginLoading(): void { this.loading.begin(); }
  private endLoading(): void { this.loading.end(); }

  get quotePageSubtitle(): string {
    if (!this.idQuote) {
      return 'Nueva cotización';
    }
    if (this.customerOnlyEditMode) {
      return 'Editar cliente';
    }
    return this.editOff ? 'Ver cotización' : 'Editar cotización';
  }

  showExtendValidityBanner(): boolean {
    // La extensión de vigencia en estándar ahora se gestiona por propuesta.
    return false;
  }

  /** Evita marcar al propietario como visitante cuando API y sesión difieren en tipo (p. ej. number vs string). */
  private isVisitorToStandardQuote(): boolean {
    return String(this.creationUser) !== String(this.authService.currentUserIds);
  }

  openExtendValidityConfirmModal(): void {
    this.modalService.component = this.extendValidityModalTemplate;
    this.modalService.openModal();
  }

  closeExtendValidityConfirmModal(): void {
    this.modalService.closeModal();
  }

  confirmExtendValidityFromModal(): void {
    this.modalService.closeModal();
    this.extendValidityFromDetail();
  }

  extendValidityFromDetail(): void {
    if (!this.idQuote || this.extendingValidity) {
      return;
    }
    this.extendingValidity = true;
    this.quoteService.extendStandardQuoteValidity(Number(this.idQuote)).pipe(
      finalize(() => {
        this.extendingValidity = false;
      }),
      takeUntilDestroyed(this._destroyRef)
    ).subscribe({
      next: (response) => {
        if (response?.data) {
          this.quoteValidityInfo = {
            valid: response.data.valid,
            minQuoteValidityDays: response.data.minQuoteValidityDays,
            validityEndDate: response.data.validityEndDate,
            validityExtensionApplied: response.data.validityExtensionApplied ?? true,
            hasInactiveFinancialSetup: response.data.hasInactiveFinancialSetup,
          };
          if (this.quoteService.formData) {
            Object.assign(this.quoteService.formData, {
              valid: response.data.valid,
              minQuoteValidityDays: response.data.minQuoteValidityDays,
              validityEndDate: response.data.validityEndDate,
              validityExtensionApplied: response.data.validityExtensionApplied ?? true,
              hasInactiveFinancialSetup: response.data.hasInactiveFinancialSetup,
            });
          }
        }
        this.showNotificationSuccess('Vigencia extendida correctamente.');
      },
      error: (error) => {
        const msg =
          error?.error?.errors?.[0] ??
          'No se pudo extender la vigencia de la cotización.';
        this.showNotificationError(typeof msg === 'string' ? msg : String(msg));
      },
    });
  }

  constructor(
    route: ActivatedRoute,
    menu: MenusServices,
    notification: NotificationService,
    public quoteService: StandardQuoteService,
    private fb: FormBuilder,
    private router: Router,
    private routee: ActivatedRoute,
    private modalService: ModalService,
    public authService: AuthService,
    public financialCalculator: FinancialCalculatorService,
    public dealerService: DealerService,
    private entitiesService: EntitiesServices,
  ) {
    super(route, menu, notification);
  }

  ngOnInit() {
    this.user = this.authService.getCurrentUserType[0];
    this.module = this.sendDataToMenu();
    this.createForm();
    this.responsive.onListen();

    this.routee.paramMap.subscribe((params) => {
      this.idQuote = params.get('id');
      if (this.idQuote) {
        const formData = this.quoteService.formData;
        if (this.quoteService.dealsClearedByCompanyChange && formData && String(formData.id) === String(this.idQuote)) {
          this.creationUser = formData.creationUser as unknown as string;
          this.isSaved = formData.status;
          const visitorCache = this.isVisitorToStandardQuote();
          this.customerOnlyEditMode =
            !visitorCache &&
            isStandardQuoteCustomerOnlyLayout(String(this.isSaved));
          this.editOff = visitorCache;
          this.quoteValidityInfo = {
            valid: (formData as any).valid,
            minQuoteValidityDays: (formData as any).minQuoteValidityDays,
            validityEndDate: (formData as any).validityEndDate,
            validityExtensionApplied: (formData as any).validityExtensionApplied,
            hasInactiveFinancialSetup: (formData as any).hasInactiveFinancialSetup,
          };
          this.form.patchValue(formData as any);
          if (visitorCache || this.customerOnlyEditMode) {
            this.form.controls['companyName'].disable();
            this.form.controls['quoteName'].disable();
            this.form.controls['dealerId'].disable();
            this.form.controls['wholeSaleId'].disable();
            this.form.controls['status'].disable();
          }
          this.syncLinkedToCreditFromQuotePayload(formData as any);
          this.deals = formData.standardQuoteDeals ?? [];
          this.ready.next({ ...formData.model, quantity: formData.quantity, creationUser: formData.creationUser, hasDeals: (formData.standardQuoteDeals?.length ?? 0) > 0 });
          this.filterDealers();
        } else {
          this.quoteService.dealsClearedByCompanyChange = false;
          this.getQuoteById();
        }
      } else {
        if (this.quoteService.formData) {
          this.form.patchValue(this.quoteService.formData);
        }
        this.filterDealers();
      }
    });

    this.form.controls[dealerBranchControlName].valueChanges.subscribe(value => {
      if (value) {
        this.form.controls[dealerBranchIdControlName].setValue(this.quoteService.formData?.wholeSaleId);
      }
    });

    // interval(20000).pipe(takeUntilDestroyed(this._destroyRef)).subscribe(()=>{
    //   console.log("Validando FORM");
    //   console.log("Validacion del valor isValid: ", { getRawValue: this.form.getRawValue() });
    //   for (const key in this.form.controls) {
    //     const control = this.form.controls[key];
    //     if (control.invalid) {
    //       console.log(`El campo ${key} tiene el siguiente error:`, control.errors);
    //     }
    //   }
    //   console.log("FIN validacion")
    // });
  }

  filterDealers() {
    const dealerId = this.form.controls[dealerIdControlName].value
    this.loading
      .track(this.entitiesService.listDealers())
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((r) => {
      this.dealers = r.data.filter((dealer: { enabled: boolean; }) => dealer.enabled === true);
      this.dealerBranches = this.dealers
        .filter((i: any) => i.id == dealerId)
        .map((i: any) => i.dealerBranches)
        .flat()
        .filter((i: any) => (i.sofomMarshId || i.dtfsMarshId) && i.enabled);
      if (this.dealerBranches.length === 0) {
        this.form.controls[dealerBranchControlName].setValue(null);
        this.form.controls[dealerBranchIdControlName].setValue(null);
        this.form.controls[dealerBranchIdControlName].disable();
      }
    });
  }

  /*getModuleAndRole() {
    if (UserType.internal === this.authService.getCurrentUserType[0]) {
      this.dealers = this.authService.getCurrentDealers;
      const defaultDealerId = this.quoteService.formData?.dealerId || 24;
      const selectedDealer = this.dealers.find((d) => d.id === defaultDealerId);

      if (selectedDealer) {
        this.form.controls[dealerIdControlName].setValue(selectedDealer.id);

        const availableBranches = selectedDealer.dealerBranches?.filter(
          (branch: DealerBranch) => (branch.dtfsMarshId || branch.sofomMarshId) && branch.enabled
        ) || [];

        this.dealerBranches = availableBranches;
        if(this.dealerBranches.length === 0){
          this.form.controls[dealerBranchControlName].setValue(null);
          this.form.controls[dealerBranchIdControlName].setValue(null);
        }else{
          const defaultBranchId = this.quoteService.formData?.wholeSaleId || availableBranches[0]?.id;
          const selectedBranch = availableBranches.find((b) => b.id === defaultBranchId);
            if (selectedBranch) {
              this.form.controls[dealerBranchControlName].setValue(selectedBranch);
              this.form.controls[dealerBranchIdControlName].setValue(selectedBranch.id);
            }else {
              this.form.controls[dealerBranchControlName].setValue(availableBranches[0]);
              this.form.controls[dealerBranchIdControlName].setValue(availableBranches[0].id);
            }
        }

      }
    } else {
      this.getRelationalName();
    }
  }*/

  getQuoteById() {
    this.quoteService.dealsClearedByCompanyChange = false;
    this.beginLoading();
    this.quoteService
      .getById(this.idQuote)
      .pipe(takeUntilDestroyed(this._destroyRef), finalize(() => { this.endLoading(); }))
      .subscribe({
      next: (response) => {
        // Log feriaCustomer del backend y después de cargar al form
        response.data.model.priceType =
          response.data.standardQuoteModel.priceType;
        this.creationUser = response.data.creationUser;
        this.form.controls['customerId'].setValue(response.data.customerId ?? null, {
          emitEvent: false,
        });
        this.form.patchValue(response.data);
        this.setAccessoriesValues(response.data.accessories);
        let price = response.data.standardQuoteModel.price;
        let model = response.data.model;
        model.price = price;
        const savedCurrency = response.data.standardQuoteModel?.currency;
        if (savedCurrency != null && savedCurrency !== '') {
          model.currency = savedCurrency;
        }
        this.form.controls['model'].setValue(model);
        this.form.controls['quantity'].setValue(response.data.quantity);

        // Sincronizar modo cliente (con/sin RFC) según customerId persistido
        this.syncClientModeAfterQuoteLoad(response.data.customerId ?? null);
        this.ready.next({
          ...response.data.model,
          quantity: response.data.quantity,
          creationUser: response.data.creationUser,
          hasDeals: response.data.standardQuoteDeals.length > 0,
        });

        this.quoteService.formData = response.data;
        this.isSaved = response.data.status;
        const visitor = this.isVisitorToStandardQuote();
        this.customerOnlyEditMode =
          !visitor && isStandardQuoteCustomerOnlyLayout(this.isSaved);
        this.editOff = visitor;
        this.quoteValidityInfo = {
          valid: response.data.valid,
          minQuoteValidityDays: response.data.minQuoteValidityDays,
          validityEndDate: response.data.validityEndDate,
          validityExtensionApplied: response.data.validityExtensionApplied,
          hasInactiveFinancialSetup: response.data.hasInactiveFinancialSetup,
        };
        //this.getModels();
        if (visitor || this.customerOnlyEditMode) {
          this.form.controls['companyName'].disable();
          this.form.controls['quoteName'].disable();
          this.form.controls['dealerId'].disable();
          this.form.controls['wholeSaleId'].disable();
          this.form.controls['status'].disable();
        }
        this.syncLinkedToCreditFromQuotePayload(response.data);
        this.filterDealers();
        this.validateUpdateCustomer();
      },
      complete: () => {
        //this.getModels();
        const visitor = this.isVisitorToStandardQuote();
        if (visitor || this.customerOnlyEditMode) {
          this.form.controls['companyName'].disable();
          this.form.controls['quoteName'].disable();
          this.form.controls['dealerId'].disable();
          this.form.controls['wholeSaleId'].disable();
          this.form.controls['status'].disable();
        }
        if (visitor) {
          this.editOff = true;
        }
        this.filterDealers();
      },
      error: (error) => {
      },
    });
  }


  models: any[] = [];

  createForm() {
    this.form = this.fb.group({
      companyName: [, [Validators.required]],
      quoteName: [, [Validators.required]],
      dealer: [, []],
      dealerId: [, []],
      id: [, []],
      customer: [, [Validators.required]],
      customerId: [, []],
      model: [, [Validators.required]],
      standardQuoteDeals: [[], Validators.required],
      accessories: this.fb.array([]),
      creationDate: [, []],
      updatedDate: [, []],
      creationUser: [, []],
      status: [, []],
      dealAmount: [, []],
      quantity: [Constants.MIN_QUANTITY_FOR_NORMAL_PRODUCT, quantityValidators],
      dealerBranch: [, []],
      wholeSaleId: [, []]
    });

    this.form.valueChanges.subscribe((value) => {
      this.deals = this.form.controls['standardQuoteDeals'].value;

      const controlIsSatisfied = (name: string): boolean => {
        const control = this.form.controls[name];
        if (!control) return false;
        return control.disabled || control.valid;
      };

      this.isValidForm =
        this.isQuoteModelComplete(this.form.controls['model'].value) &&
        this.form.controls['customer'].valid &&
        this.form.controls['customerId'].valid &&
        this.form.controls['quantity'].valid &&
        controlIsSatisfied('companyName') &&
        controlIsSatisfied('quoteName') &&
        controlIsSatisfied('dealer') &&
        controlIsSatisfied('dealerBranch');

      //para habilitar y deshabilitar el boton de guardado
      this.saved =
        this.isValidForm &&
        this.form.controls['standardQuoteDeals'].valid &&
        !this.hasFinancialSetupCompanyMismatchDeal();

      if (this.deals && this.deals.length > 0) {
        this.isSelectedDeal = this.deals.some(
          (deal) => deal.isSelected == true
        );
      } else {
        this.isSelectedDeal = false;
      }
    });

    this.form.controls['customer'].valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        const cust = this.form.controls['customer'].getRawValue();
        this.quoteService.formData = {
          ...(this.quoteService.formData ?? (this.form.getRawValue() as any)),
          customer: cust,
        };
      });

    this.form.controls['customer'].valueChanges
      .pipe(
        map(() => {
          const cust = this.form.controls['customer'].getRawValue();
          return Boolean(cust?.feriaCustomer);
        }),
        distinctUntilChanged(),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(() => this.quoteService.customerLoyaltyFeriaChanged.next());
  }

  validateUpdateCustomer() {
    const customerId = this.form.controls['customerId'].value;
    if (customerId == null) {
      return;
    }
    const customer = this.form.controls['customer'].value;
    // Only validate if we have RFC (mode "with RFC")
    if(customer !== null && customer?.rfc !== null && customer?.rfc !== '' && !customer?.isFromProLeaseNet){
      this.clientsService.searchClientByRFC(customer.rfc).pipe(takeUntilDestroyed(this._destroyRef)).subscribe((res)=>{
        const merged = {
          ...customer,
          ...res.data,
          isFromProLeaseNet: res.data.isFromProLeaseNet,
        };
        const normalized =
          merged.personType === 'NATURAL_PERSON'
            ? {
                ...merged,
                ...this.clientsService.resolveNaturalPersonNameFields(merged),
                customerName: null,
              }
            : merged;
        this.form.controls['customer'].setValue(normalized);
      });
    }
  }

  getRelationalName() {
    const dealerId = this.authService.getCurrentDealerId;
    this.loading
      .track(this.quoteService.getRelationalName(dealerId))
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((response) => {
      this.dealers = response.data;
      if (this.dealers.length === 1) {
        if (this.dealers[0].dealerBranches.length == 1) {
          if(this.dealers[0].dealerBranches[0].enabled === false){
          this.form.controls[dealerBranchControlName].setValue(null)
          this.dealerBranchName = 'El distribuidor no tiene sucursales habilitadas.'
          }else{
          this.form.controls[dealerBranchControlName].setValue(this.dealers[0].dealerBranches[0])
          this.form.controls[dealerBranchIdControlName].setValue(this.dealers[0].dealerBranches[0].id)
          this.dealerBranchName = this.dealers[0].dealerBranches[0].name
          }
        } else {
          this.dealerBranches = this.dealers[0].dealerBranches
          this.dealerBranchName = this.dealers[0].dealerBranches[0].name

        }
      } else {
        this.dealerBranches = this.dealers.find((i) => i.id == dealerId)?.dealerBranches ?? [];
      }
    })
  }

  onChangeDealer(): void {
    const dealerId = this.form.controls[dealerIdControlName].value
    this.dealerBranches = []
    let filter: DealerBranch[] = this.dealers
      .filter((i: any) => i.id == dealerId)
      .map((i: Dealer) => i.dealerBranches)
      .flat()
      .filter((i: any) => (i.sofomMarshId || i.dtfsMarshId) && i.enabled);

    this.dealerBranches = filter;
    if (filter.length >= 1) {
      this.form.controls[dealerBranchControlName].setValue(filter[0]);
      this.form.controls[dealerBranchIdControlName].setValue(filter[0].id);
      this.form.controls[dealerBranchIdControlName].enable();
    }
    if (filter.length === 0) {
      this.form.controls[dealerBranchControlName].setValue(null);
      this.form.controls[dealerBranchIdControlName].setValue(null);
      this.form.controls[dealerBranchIdControlName].disable();
    }

  }


  setAccessoriesValues(accessoriesData: any[]) {
    const accessoriesArray = this.form.get('accessories') as FormArray;
    accessoriesArray.clear();

    accessoriesData.forEach((accessory) => {
      const accessoryFormGroup = this.fb.group({
        id: [accessory.id],
        price: [accessory.price],
        classification: [accessory.classification],
        year: [accessory.year],
        model: [accessory.model],
        quantity: [accessory.quantity],
      });
      accessoriesArray.push(accessoryFormGroup);
    });
  }

  update(isDownload?: boolean) {
    if (this.linkedToCreditRequest) {
      this.showNotificationError('Esta cotización está vinculada a una solicitud de crédito y no se puede editar.');
      return;
    }
    // Obtener el valor del formulario y almacenarlo en formData
    const formData = { ...this.form.getRawValue() } as any;

    this.applyCustomerPayloadForSave(formData);

    if (isDownload) {
      formData.status = statusType.saved;
      this.form.controls['status'].setValue('SAVED');
    }

    this.quoteService.formData = formData;
    const userId = this.authService.currentUserIds;
    const isOwner = userId === this.form.controls['creationUser'].value;

    const runSaveAfterPrepare = () => {
      this.setValueInService();

      if (!isOwner) {
        if (isDownload) {
          this.downloadPdf();
        }
        return;
      }

      const quoteId = this.form.controls['id'].value;
      const update$ = this.quoteService.dealsClearedByCompanyChange
        ? this.quoteService.deleteAllDeals(this.idQuote).pipe(
            switchMap(() => this.quoteService.updateQuote(quoteId))
          )
        : this.quoteService.updateQuote(quoteId);

      this.loading.track(update$).subscribe({
        next: (response) => {
          this.quoteService.dealsClearedByCompanyChange = false;
          this.showNotificationSuccess(
            'La cotización se ha guardado correctamente.'
          );
          if (isDownload) {
            this.downloadPdf();
          }
        },
        error: (error) => {
          console.error('Error al guardar la cotización:', error);
          this.showNotificationError(
            'Ha ocurrido un error al momento de guardar la cotización.'
          );
        },
      });
    };

    if (isDownload && isOwner) {
      this.loading
        .track(
          this.recalculateDealsMissingResultBeforePdf().pipe(
            takeUntilDestroyed(this._destroyRef),
            catchError((err) => {
              if (err?.message !== 'marsh') {
                this.showNotificationError(
                  'No se pudieron recalcular las propuestas antes del PDF. Revise los datos e intente de nuevo.'
                );
              }
              return EMPTY;
            })
          )
        )
        .subscribe({
          next: () => runSaveAfterPrepare(),
        });
    } else {
      runSaveAfterPrepare();
    }
  }

  /** Propuesta clonada o sin resultado: hace falta cotizar antes de persistir el PDF. */
  private dealMissingCalculatedResult(deal: any): boolean {
    return !deal?.result || deal?.clonedPendingRecalculate === true;
  }

  /**
   * Seguro financiado multianual/anual con paquete requiere el flujo completo en pantalla de propuesta (forkJoin seguro + cotización).
   */
  private dealNeedsMarshRecalcInDealScreen(deal: any): boolean {
    if (!this.dealMissingCalculatedResult(deal)) {
      return false;
    }
    const ins = deal?.insurance;
    if (!ins?.packageList?.coverageListModel?.length) {
      return false;
    }
    const t = ins.insuranceType;
    return (
      t === InsuranceTypeEnum.FINANCED_ANNUAL ||
      t === InsuranceTypeEnum.FINANCED_MULTIYEAR
    );
  }

  private applyCalculationResponseToDeal(deal: any, payload: any): any {
    const { payments, ...rest } = payload;
    return {
      ...deal,
      clonedPendingRecalculate: false,
      result: {
        term: rest.term,
        unitPrice: rest.unitPrice,
        unitPriceNoTax: rest.unitPriceNoTax,
        annualPercentageRate: rest.annualPercentageRate,
        downpaymentAmount: rest.downpaymentAmount,
        openingfeeAmount: rest.openingfeeAmount,
        balloonAmount: rest.balloonAmount,
        guaranteeDepositAmount: rest.guaranteeDepositAmount,
        insuranceAmount: rest.insuranceAmount,
        additionalServicesAmount: rest.additionalServicesAmount,
        confirmationExpensesAmount: rest.confirmationExpensesAmount,
        initialPayment: rest.initialPayment,
        financialAmount: rest.financialAmount,
        monthlyPayment: rest.monthlyPayment,
        annualTotalCost: rest.annualTotalCost,
        purchaseOptionAmount: rest.purchaseOptionAmount,
        residualValue: rest.residualValue,
      },
      payments: payments ?? deal.payments ?? [],
    };
  }

  private recalculateDealsMissingResultBeforePdf() {
    const raw = this.form.getRawValue() as any;
    const models = raw.model;
    const standardQuoteModel =
      raw.standardQuoteModel ?? this.quoteService.formData?.standardQuoteModel;
    const clientComponent = this.responsive.isMobileSignal()
      ? this.clientMobile
      : this.clientDesktop;
    let customer = raw.customer;
    if (clientComponent?.formCustomer) {
      customer = clientComponent.transformCustomerData(
        clientComponent.formCustomer.getRawValue()
      );
    }

    const deals: any[] = [...(raw.standardQuoteDeals || [])];

    const marshBlocked = deals.some((d) =>
      this.dealNeedsMarshRecalcInDealScreen(d)
    );
    if (marshBlocked) {
      this.showNotificationError(
        'Una propuesta clonada tiene seguro financiado con cotización de cobertura. Abra la propuesta, pulse Calcular y luego descargue el PDF.'
      );
      return throwError(() => new Error('marsh'));
    }

    const indices: number[] = [];
    deals.forEach((d, i) => {
      if (this.dealMissingCalculatedResult(d)) {
        indices.push(i);
      }
    });

    if (indices.length === 0) {
      return of(undefined);
    }

    this.quoteService.formData = {
      ...this.quoteService.formData,
      ...raw,
      customer,
      standardQuoteModel,
    };

    const requests = indices.map((i) =>
      this.quoteService
        .calculateQuote(deals[i], models, customer, standardQuoteModel)
        .pipe(
          map((response: any) => {
            if (!response?.status) {
              const msg =
                response?.errors?.[0] ||
                response?.error?.Detalle ||
                'Error al calcular';
              throw new Error(typeof msg === 'string' ? msg : 'calculate');
            }
            return { index: i, payload: response.data[0] };
          })
        )
    );

    return forkJoin(requests).pipe(
      map((results) => {
        const nextDeals = [...deals];
        for (const { index, payload } of results) {
          nextDeals[index] = this.applyCalculationResponseToDeal(
            nextDeals[index],
            payload
          );
        }
        this.form.controls['standardQuoteDeals'].setValue(nextDeals);
        this.deals = nextDeals;
        this.quoteService.formData = {
          ...this.quoteService.formData,
          ...this.form.getRawValue(),
          customer,
          standardQuoteModel,
        };
        return undefined;
      })
    );
  }

  isEditingMode(): boolean {
    return this.deals.length > 0 && this.idQuote;
  }

  saveDraft() {
    this.quoteService.formData = { ...this.form.getRawValue() } as any;
    if (this.quoteService.formData) {
      this.applyCustomerPayloadForSave(this.quoteService.formData);
    }
    this.loading.track(this.quoteService.saveDraft()).pipe(takeUntilDestroyed(this._destroyRef)).subscribe({
      next: (response) => {
        // this.setResponseOnForm(response.data);
      },
      error: (error) => {
      },
    });
  }

  downloadPdf() {
    this.loading.track(this.quoteService.downloadPdf(this.idQuote)).pipe(takeUntilDestroyed(this._destroyRef)).subscribe({
      next: (blob: Blob) => {
        this.saveAsPdf(blob, `Cotizacion_${this.idQuote}`);
        this.ngOnInit();
      },
      error: (blob: Blob) => {
        this.showNotificationError(
          'Ha ocurrido un error no se pudo descargar pdf.'
        );
      },
    });
  }

  setValueInService() {
    this.setValueDownpaymentAndOpeningfee();
    const rawFormValue = this.form.getRawValue();
    rawFormValue.quantity = normalizeQuoteQuantity(rawFormValue.quantity);
    if (rawFormValue.model) {
      rawFormValue.model = {
        ...rawFormValue.model,
        quantity: normalizeQuoteQuantity(rawFormValue.model.quantity),
      };
    }
    this.form.controls['quantity'].setValue(rawFormValue.quantity, {
      emitEvent: false,
    });

    const existingCustomer = this.quoteService.formData?.customer;
    this.quoteService.formData = rawFormValue;
    if (existingCustomer && this.quoteService.formData) {
      this.quoteService.formData.customer = existingCustomer;
    }
    if (this.quoteService.formData) {
      this.applyCustomerPayloadForSave(this.quoteService.formData);
    }
  }

  /** Alinea customer/customerId al guardar: sin RFC implica customerId null. */
  private applyCustomerPayloadForSave(formData: any): void {
    const clientComponent = this.responsive.isMobileSignal()
      ? this.clientMobile
      : this.clientDesktop;
    if (!clientComponent?.formCustomer) {
      return;
    }
    formData.customer = clientComponent.getRawValue();
    if (!clientComponent.isClientWithRFC()) {
      formData.customerId = null;
      this.form.controls['customerId'].setValue(null, { emitEvent: false });
    }
  }

  private syncClientModeAfterQuoteLoad(customerId: number | null): void {
    this.form.controls['customerId'].setValue(customerId, { emitEvent: false });
    const clientComponent = this.responsive.isMobileSignal()
      ? this.clientMobile
      : this.clientDesktop;
    clientComponent?.applyCustomerModeFromQuote(customerId);
  }

  setValueDownpaymentAndOpeningfee() {
    let deals = this.form.controls['standardQuoteDeals'].value;
    let newDeals = deals.map((i: any) => {
      return {
        ...i,
        financialSetup: {
          ...i.financialSetup,
          loyaltyFinancialPackage:
            i.financialSetup?.loyaltyFinancialPackage ??
            i.standardFinancialSetup?.loyaltyFinancialPackage ??
            false,
          feriaFinancialPackage: false,
          // Prefer current financialSetup values (user-edited) over legacy top-level fields.
          downPayment: i.financialSetup?.downPayment ?? i.downPayment ?? i.standardFinancialSetup?.downPayment,
          downPaymentTax: i.financialSetup?.downPaymentTax ?? i.downPaymentTax ?? i.standardFinancialSetup?.downPaymentTax,
          downPaymentTaxType: i.financialSetup?.downPaymentTaxType ?? i.downPaymentTaxType ?? i.standardFinancialSetup?.downPaymentTaxType,
          openingFee: i.financialSetup?.openingFee ?? i.openingFee ?? i.standardFinancialSetup?.openingFee,
          openingFeeTax: i.financialSetup?.openingFeeTax ?? i.openingFeeTax ?? i.standardFinancialSetup?.openingFeeTax,
          openingFeeTaxType: i.financialSetup?.openingFeeTaxType ?? i.openingFeeTaxType ?? i.standardFinancialSetup?.openingFeeTaxType,
          termId: i.financialCondition.id,
        },
      };
    });
    this.form.controls['standardQuoteDeals'].patchValue(newDeals);
    this.modalService.closeModal();
  }

  cancel() {
    this.router.navigate(['/quote/standard-quote']);
  }

  ngOnDestroy() {
    this._quote = null;
  }

  setQuantity(quantity: any) {
    const qty = normalizeQuoteQuantity(quantity);
    this.form.controls['quantity'].setValue(qty);
    this.quoteService.updatedFormData.next(this.form.getRawValue());
  }

  status() {
    const form = this.form;
    for (const controlName of Object.keys(form.controls)) {
      const control = form.get(controlName);

      if (control && control.invalid) {
      }
    }
  }

  updateDeal(deals: any) {
    this.form.controls['standardQuoteDeals'].reset();
    this.form.controls['standardQuoteDeals'].setValue(deals);
    this.deals = deals;
    this.quoteService.formData = this.form.getRawValue();
    // this.update()
  }


  openModal() {
    if (this.linkedToCreditRequest) {
      return;
    }
    if (!this.isEditingMode()) {
      return;
    }
    // No actualizar formData aquí: al cancelar se debe restaurar la compañía anterior.
    // Si el usuario confirma (Eliminar), deleteAllDeals -> setValueInService actualizará formData.
    this.modalService.component = this.modalTemplate;
    this.modalService.openModal();
  }

  onInputFocus() {
    this.openModal();
  }

  openRequestedModal() {
    this.openModal();
  }

  cancelChanges() {
    if (!this.quoteService.formData) return;
    this.form.patchValue(this.quoteService.formData);
    this.modalService.closeModal();
  }


  confirmCompanyChangeClearDealsLocally() {
    if (this.linkedToCreditRequest) {
      this.showNotificationError('Esta cotización está vinculada a una solicitud de crédito y no se puede editar.');
      return;
    }
    this.quoteService.dealsClearedByCompanyChange = true;
    this.form.controls['standardQuoteDeals'].setValue([]);
    this.setValueInService();
    this.modalService.closeModal();
    this.showNotificationSuccess(
      'Propuestas quitadas. Guarde la cotización para aplicar el cambio; si cancela sin guardar, las propuestas se conservarán.'
    );
  }

  private isQuoteModelComplete(model: unknown): boolean {
    const m = model as { id?: unknown; name?: unknown; modelCode?: unknown } | null | undefined;
    return !!(m?.id && m?.name && m?.modelCode);
  }

  deleteAllDeals() {
    if (this.linkedToCreditRequest) {
      this.showNotificationError('Esta cotización está vinculada a una solicitud de crédito y no se puede editar.');
      return;
    }
    const clearDealsLocally = () => {
      this.form.controls['standardQuoteDeals'].setValue([]);
      this.deals = [];
      this.isSelectedDeal = false;
      this.setValueInService();
    };
    if (!this.idQuote) {
      clearDealsLocally();
      return;
    }
    this.loading
      .track(
        this.quoteService
          .deleteAllDeals(this.idQuote)
          .pipe(finalize(() => { this.modalService.closeModal(); }))
      )
      .subscribe({
      next: (response) => {
        clearDealsLocally();
        this.showNotificationSuccess(
          'Las propuestas se han eliminado correctamente.'
        );
      },
      error: (error) => {
        this.showNotificationError(
          'Ha ocurrido un error al momento de eliminar las porpuestas.'
        );
      },
    });
  }

  updateForm(value: any) {
    this.form.patchValue(value);
  }

  isInvalidQuote() {
    const dataValue = this.form.getRawValue();
    let invalid = false;
    invalid = invalid || (dataValue.model?.enabled === false && dataValue.status === 'DRAFT');
    return invalid;
  }

  setCustomerId(customerId: number | null) {
    this.form.controls['customerId'].setValue(customerId);
  }


  get formCustomerId(): number | null {
    return this.form.controls['customerId'].value;
  }

  get financialComponentId(): string {
    return this.responsive.isMobileSignal() ? 'financialServiceEditMobile' : 'financialServiceEditDesktop';
  }

  /** Usa `linkedToCreditRequest` del quote-service (detalle/listado); ya no se llama a credit-request-service. */
  private syncLinkedToCreditFromQuotePayload(data: any | null | undefined): void {
    if (!data) {
      this.linkedToCreditRequest = false;
      return;
    }
    const linked = Boolean(data.linkedToCreditRequest);
    this.linkedToCreditRequest = linked;
    if (linked) {
      this.customerOnlyEditMode = false;
      this.editOff = true;
      try {
        Object.values(this.form?.controls ?? {}).forEach((c: any) =>
          c?.disable?.(),
        );
      } catch {
        // noop
      }
    }
  }

  private hasFinancialSetupCompanyMismatchDeal(): boolean {
    const quoteCompany = this.form.getRawValue()?.companyName;
    const deals = this.form.controls['standardQuoteDeals']?.value ?? [];
    return deals.some(
      (deal: any) =>
        deal?.isFinancialSetupCompanyMismatch === true ||
        isFinancialSetupCompanyMismatchForQuote(
          deal?.financialSetup?.companyName,
          quoteCompany,
        ),
    );
  }

}
